import React, { createContext, useContext, useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'
import type { PhotoCandid } from '@/api/types/photo'
import { photosService } from '@/api/services/photos.service'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'

import type { CenterCanvasHandle } from '@/components/app/editor/center-canvas'
import { buildPlacedPhotoShape } from '@/lib/photos/build-placed-photo-shape'
import { computePhotoPlacementSize } from '@/lib/photos/photo-placement-geometry'
import {
  hasPreferredCanvasPhotoUrl,
  mergeCandidDetailIntoPhoto,
} from '@/lib/photos/resolve-canvas-photo-url'
import {
  PHOTO_DRAG_GHOST_OPACITY,
  PHOTO_KEYBOARD_DEFAULT_CENTER_FRACTION,
  PHOTO_TOUCH_LONG_PRESS_MS,
} from '@/lib/photos/photo-placement-config'
import { LINKED_TEXT_PHOTO_DROP_BLOCKED_MESSAGE } from '@/lib/photos/linked-text-photo-drop-policy'
import {
  type SpreadBleedSize,
  reconcilePhotoPlacementOverridesWithShapes,
} from '@/lib/photos/sync-photo-placement-from-shapes'
import { notifyPhotoPlacementChangedOnSpread } from '@/lib/photos/schedule-album-photos-refresh'
import type { GridSnapLiveRect } from '@/lib/guides/grid-snap'
import { PhotoDragGhost } from '@/components/app/photos/photo-drag-ghost'
import {
  type PlacementCanvasApi,
  type PlacementDropBlockedReason,
  type PlacementDropHit,
  type PlacementIntrinsicSize,
  type PlacementItemControls,
  usePlacementSession,
} from '@/hooks/use-placement-session'

/** Why a hit drop target may not receive the photo. Currently only text-link chain membership. */
export type PhotoDropBlockedReason = PlacementDropBlockedReason

/**
 * The topmost drop target under the cursor. A blocked target is still reported — it suppresses the
 * valid-target highlight and refuses the drop outright, rather than letting the photo fall through
 * to standalone placement or to whatever sits behind it (WMPCM-8516).
 */
export type PhotoDropHit = PlacementDropHit

export type PhotoPlacementCanvasApi = PlacementCanvasApi

type PhotoPlacementContextValue = {
  beginPointerDrag: (
    photo: PhotoCandid,
    clientX: number,
    clientY: number,
    thumbRect: DOMRect,
    pointerId: number,
    pointerType?: string,
    pointerCaptureEl?: HTMLElement | null
  ) => void
  beginKeyboardCarry: (photo: PhotoCandid, thumbRect: DOMRect) => void
  cancelDrag: () => void
  isDraggingPhoto: boolean
  draggingCandidId: string | null
  isKeyboardCarry: boolean
  registerCanvasApi: (api: PhotoPlacementCanvasApi | null) => void
  /**
   * shapeId of the shape under the cursor that is a valid photo drop target, or null. Stays null
   * over a blocked target, so the ghost never advertises a drop that will be refused.
   */
  dropTargetShapeId: string | null
}

const PhotoPlacementContext = createContext<PhotoPlacementContextValue | null>(null)

export function usePhotoPlacement(): PhotoPlacementContextValue {
  const ctx = useContext(PhotoPlacementContext)

  if (!ctx) {
    throw new Error('usePhotoPlacement must be used within PhotoPlacementProvider')
  }

  return ctx
}

export function usePhotoPlacementOptional(): PhotoPlacementContextValue | null {
  return useContext(PhotoPlacementContext)
}

type PhotoPlacementProviderProps = {
  children: React.ReactNode
  shapeManagerRef: React.RefObject<ShapeManagerRef | null>
  centerCanvasRef: React.RefObject<CenterCanvasHandle | null>
  spreadIdRef: React.MutableRefObject<string>
  /** The spread box and, for a single-page spread, which half is the page. Without it a drop
   *  counts as on whichever half of the box it touches. */
  spreadPlacementBounds?: SpreadBleedSize
  snapToGrid?: boolean
  gridSnapStep?: number
  gridSnapLiveRects?: GridSnapLiveRect[]
  onScreenReaderAnnounce?: (message: string) => void
  /** Called when the user drops a photo onto a fillable shape. */
  onFillShape?: (
    shapeId: string,
    photo: PhotoCandid,
    kindId: number,
    dropCanvasPoint: { canvasX: number; canvasY: number },
    intrinsicSize: { width: number; height: number } | null
  ) => void
  /**
   * Called when the drop landed on a target that refuses photos. Nothing is placed. The toast is
   * raised by the consumer (`home.tsx`), matching how every other user-facing refusal in the
   * editor is surfaced.
   */
  onBlockedDrop?: (reason: PhotoDropBlockedReason) => void
}

function loadIntrinsicSize(url: string): Promise<PlacementIntrinsicSize | null> {
  return new Promise((resolve) => {
    const img = new Image()

    img.onload = () => {
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight,
      })
    }

    img.onerror = () => resolve(null)
    img.src = url
  })
}

/**
 * `hasRealResolutionRef` is a per-drag ordering guard shared with `primeRealResolution`: once
 * the real (API) resolution has landed, the thumbnail load must never overwrite it, even if the
 * thumbnail `<img>` decode happens to resolve later (plausible when it isn't already browser-
 * cached — there's no ordering guarantee between the two fire-and-forget fetches).
 */
function primeIntrinsicSize(
  url: string,
  sizeRef: React.MutableRefObject<PlacementIntrinsicSize | null>,
  hasRealResolutionRef: React.MutableRefObject<boolean>
): void {
  void loadIntrinsicSize(url).then((size) => {
    if (size && !hasRealResolutionRef.current) {
      sizeRef.current = size
    }
  })
}

type CandidDetail = Awaited<ReturnType<typeof photosService.getCandidDetail>>

/**
 * Overwrites the (thumbnail-derived) intrinsic size with the photo's real native pixel
 * dimensions once the candid detail API resolves — `urlSqCrop` is a square-crop thumbnail, so
 * its dimensions are both smaller than and a different aspect ratio than the source photo.
 * Also merges `urlScr` / preview / primary onto the in-flight photo so drop uses the sharp
 * canvas asset (legacy odctrl), not the tray thumb.
 * Fire-and-forget: if this hasn't resolved by drop time, the thumbnail-derived size already in
 * place is used instead (never blocks the drop waiting on network). Sets `hasRealResolutionRef`
 * first so a late-arriving `primeIntrinsicSize` resolution can never overwrite this value.
 */
function primeRealResolution(
  candidId: string,
  sizeRef: React.MutableRefObject<PlacementIntrinsicSize | null>,
  hasRealResolutionRef: React.MutableRefObject<boolean>,
  mergePhotoUrls: (candidId: string, detail: CandidDetail) => void
): void {
  void photosService
    .getCandidDetail(candidId)
    .then((detail) => {
      mergePhotoUrls(candidId, detail)

      if (detail.pixelWidth && detail.pixelHeight) {
        hasRealResolutionRef.current = true
        sizeRef.current = { width: detail.pixelWidth, height: detail.pixelHeight }
      }
    })
    .catch(() => {
      // Keep the thumbnail-derived size already primed by primeIntrinsicSize.
    })
}

/** Ensure tray photo carries screen/preview URLs before canvas place/fill (detail may already be cached). */
async function ensurePhotoHasCanvasUrls(photo: PhotoCandid): Promise<PhotoCandid> {
  if (hasPreferredCanvasPhotoUrl(photo)) return photo

  try {
    const detail = await photosService.getCandidDetail(photo.candidId)

    return mergeCandidDetailIntoPhoto(photo, detail)
  } catch {
    return photo
  }
}

/** Merges a late-arriving candid detail onto the in-flight photo, if it is still the dragged one. */
function mergePhotoUrlsFromDetail(
  controls: PlacementItemControls<PhotoCandid>,
  candidId: string,
  detail: CandidDetail
): void {
  const { current } = controls.itemRef

  if (!current || current.candidId !== candidId) return

  controls.setItem(mergeCandidDetailIntoPhoto(current, detail))
}

export function PhotoPlacementProvider({
  children,
  shapeManagerRef,
  centerCanvasRef,
  spreadIdRef,
  spreadPlacementBounds,
  snapToGrid,
  gridSnapStep,
  gridSnapLiveRects,
  onScreenReaderAnnounce,
  onFillShape,
  onBlockedDrop,
}: PhotoPlacementProviderProps) {
  const intrinsicSizeRef = useRef<PlacementIntrinsicSize | null>(null)
  const spreadPlacementBoundsRef = useRef(spreadPlacementBounds)

  spreadPlacementBoundsRef.current = spreadPlacementBounds
  const hasRealResolutionRef = useRef(false)

  const session = usePlacementSession<PhotoCandid>({
    centerCanvasRef,
    intrinsicSizeRef,
    snapToGrid,
    gridSnapStep,
    gridSnapLiveRects,
    onScreenReaderAnnounce,
    onBlockedDrop,
    blockedDropMessage: LINKED_TEXT_PHOTO_DROP_BLOCKED_MESSAGE,
    keyboardCenterFraction: PHOTO_KEYBOARD_DEFAULT_CENTER_FRACTION,
    touchLongPressMs: PHOTO_TOUCH_LONG_PRESS_MS,
    displayNameOf: (target) => target.displayName,
    onDragStart: (target, controls) => {
      primeIntrinsicSize(target.urlSqCrop, intrinsicSizeRef, hasRealResolutionRef)
      primeRealResolution(
        target.candidId,
        intrinsicSizeRef,
        hasRealResolutionRef,
        (candidId, detail) => mergePhotoUrlsFromDetail(controls, candidId, detail)
      )
    },
    onReset: () => {
      hasRealResolutionRef.current = false
    },
    placeAtCanvas: (target, centerX, centerY, { api, announce }) => {
      const mgr = shapeManagerRef.current

      if (!mgr) return

      void (async () => {
        const photoForPlace = await ensurePhotoHasCanvasUrls(target)
        // Prefer server-reported pixels (tray/detail payload), then natural size primed during drag.
        const pixelWidth = photoForPlace.pixelWidth ?? intrinsicSizeRef.current?.width
        const pixelHeight = photoForPlace.pixelHeight ?? intrinsicSizeRef.current?.height
        const shape = buildPlacedPhotoShape({
          objectId: crypto.randomUUID().replace(/-/g, ''),
          photo: photoForPlace,
          centerX,
          centerY,
          canvasWidth: api.bleedWidth,
          canvasHeight: api.bleedHeight,
          intrinsicWidth: pixelWidth,
          intrinsicHeight: pixelHeight,
          size: computePhotoPlacementSize(pixelWidth, pixelHeight),
        })

        mgr.addShapeData({ ...shape, placementAnimateIn: true }, { selectAfterCreate: true })

        // Placed badge / page tracking only when the photo intersects the spread (not
        // pasteboard-only).
        reconcilePhotoPlacementOverridesWithShapes(
          mgr.getShapes(),
          spreadPlacementBoundsRef.current ?? {
            bleedWidth: api.bleedWidth,
            bleedHeight: api.bleedHeight,
          },
          spreadIdRef.current
        )
        // The reconcile above only updates the LOCAL optimistic override — tell the tray to
        // re-fetch so its server-cached `placedFlag` catches up too (same pattern as removal's
        // `notifyPhotoPlacementChangedOnSpread` call in `home.tsx`'s `handleRemovePhotoFromShape`).
        notifyPhotoPlacementChangedOnSpread()
        announce(`Placed ${photoForPlace.displayName} on canvas`)
      })()
    },
    fillShape: (target, hit, point, { announce }) => {
      void ensurePhotoHasCanvasUrls(target).then((photoForFill) => {
        // Prefer server-reported pixels (tray/detail payload) over the thumbnail-derived probe —
        // both are fire-and-forget and may not have resolved by drop time on a fast drag (see
        // primeIntrinsicSize/primeRealResolution above); falling back to null here (rather than
        // the API's own pixelWidth/pixelHeight, as placeAtCanvas already does) writes a
        // photo-fill size that ignores the photo's real aspect ratio.
        const intrinsicSize =
          photoForFill.pixelWidth && photoForFill.pixelHeight
            ? { width: photoForFill.pixelWidth, height: photoForFill.pixelHeight }
            : intrinsicSizeRef.current

        onFillShape?.(hit.shapeId, photoForFill, hit.kindId, point, intrinsicSize)
        announce(`Filled shape with ${photoForFill.displayName}`)
      })
    },
  })

  const { item, beginPointerDrag, beginKeyboardCarry, cancelDrag, registerCanvasApi } = session
  const { isActive, isKeyboardCarry, dropTargetShapeId, ghost } = session

  const contextValue = useMemo<PhotoPlacementContextValue>(
    () => ({
      beginPointerDrag,
      beginKeyboardCarry,
      cancelDrag,
      isDraggingPhoto: isActive,
      draggingCandidId: item?.candidId ?? null,
      isKeyboardCarry,
      registerCanvasApi,
      dropTargetShapeId,
    }),
    [
      beginKeyboardCarry,
      beginPointerDrag,
      cancelDrag,
      dropTargetShapeId,
      isActive,
      isKeyboardCarry,
      item?.candidId,
      registerCanvasApi,
    ]
  )

  return (
    <PhotoPlacementContext.Provider value={contextValue}>
      {children}
      {item != null &&
        ghost != null &&
        createPortal(
          <PhotoDragGhost photo={item} opacity={PHOTO_DRAG_GHOST_OPACITY} {...ghost} />,
          document.body
        )}
    </PhotoPlacementContext.Provider>
  )
}
